import type { ReactNode } from 'react';
import type { MetricViewIssueType } from '@/hooks/use-metric-view-filters';
import { METRIC_VIEW_CHART_HEIGHT } from '@/lib/metric-view/responsive-thresholds';

export const COLOR_STORY = 'var(--color-chart-8)';
export const COLOR_BUG = 'var(--color-chart-10)';
export const COLOR_TASK = 'var(--color-chart-4)';

export const ISSUE_TYPE_COLOR: Record<'Story' | 'Bug' | 'Task', string> = {
  Story: COLOR_STORY,
  Bug: COLOR_BUG,
  Task: COLOR_TASK,
};

export function LegendChip({ color, label }: Readonly<{ color: string; label: string }>) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

export function IssueTypeLegend({ types }: Readonly<{ types?: Array<'Story' | 'Bug' | 'Task'> }>) {
  const active = types ?? ['Story', 'Bug', 'Task'];
  return (
    <div className="flex items-center gap-4">
      {active.includes('Story') && <LegendChip color={COLOR_STORY} label="Story" />}
      {active.includes('Bug') && <LegendChip color={COLOR_BUG} label="Bug" />}
      {active.includes('Task') && <LegendChip color={COLOR_TASK} label="Task" />}
    </div>
  );
}

/** Centered chart-height placeholder for prompt / empty / unsupported states. */
export function ChartEmptyState({ message }: Readonly<{ message: string }>) {
  return (
    <div
      className="flex items-center justify-center"
      style={{ height: METRIC_VIEW_CHART_HEIGHT }}
    >
      <p className="text-center text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

/**
 * Client-side narrowing for the issue-type pill. Epic never matches — epic
 * cycle time is not tracked per issue, so cards short-circuit before this.
 */
export function matchesIssueTypeFilter(issueType: string, active: MetricViewIssueType): boolean {
  if (active === 'all') return true;
  if (active === 'story') return issueType === 'Story';
  if (active === 'bug') return issueType === 'Bug';
  if (active === 'task') return issueType === 'Task';
  return false;
}

export function CardHeaderBlock({
  title,
  description,
  children,
}: Readonly<{ title: string; description?: string; children?: ReactNode }>) {
  return (
    <div className="space-y-1">
      <h3 className="text-lg font-semibold leading-none tracking-tight">{title}</h3>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      {children && <div className="pt-2">{children}</div>}
    </div>
  );
}
